import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Banknote, ChevronDown, CreditCard, Crown, Ellipsis, KeyRound, Landmark, LayoutDashboard, ListChecks, LogOut, MessagesSquare, Package, Route, Search, Send, ShieldCheck, User, UserPlus,
} from 'lucide-react';
import { LOGOUT_FAILED, useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import NotificationsDropdown from '@/components/NotificationsDropdown';
import '@/styles/verification.css';
import '@/styles/dashboard.css';
import '@/styles/app-header.css';
import '@/styles/cards.css';
import '@/styles/mission-list-card.css';
import '@/styles/detail-page.css';
import '@/styles/dispute.css';
import { isEntreprise } from '@/utils/entreprise';
import { BACKEND_URL } from '@/config';

const API = `${BACKEND_URL}/api`;
const PHONE_MAX = 640;

function getInitials(user) {
  if (!user) return '?';
  return `${(user.first_name || '')[0] || ''}${(user.last_name || '')[0] || ''}`.toUpperCase();
}

const VIEW_LABEL = { sender: 'Expéditeur', carrier: 'Transporteur' };
const badgeText = (n) => (n > 99 ? '99+' : String(n));

/**
 * Cadre des pages de l'application (direction C, docs/design/ecarts-menu.md) : en-tête horizontal en pastilles, menu
 * du compte sous l'avatar, cloche des notifications ; sur téléphone, barre du bas et volet « Plus ».
 * Les pastilles suivent la vue (`profileView` : rôle du compte, ou `active_role` d'un compte hybride) ; le menu du compte
 * suit les rôles, pour qu'un compte hybride garde l'accès à l'autre côté sans changer de vue.
 */
export default function AppShell({ children }) {
  const { user, logout, switchRole } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [switching, setSwitching] = useState(false);
  const [propsBadge, setPropsBadge] = useState(0);
  const [msgBadge, setMsgBadge] = useState(0);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [isPhone, setIsPhone] = useState(() => typeof window !== 'undefined' && window.innerWidth < PHONE_MAX);
  const accountRef = useRef(null);
  const accountItems = useRef([]);

  useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth < PHONE_MAX);
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => { setSheetOpen(false); setAccountOpen(false); }, [location.pathname, location.search]);

  useEffect(() => {
    if (!accountOpen) return undefined;
    const outside = (e) => { if (accountRef.current && !accountRef.current.contains(e.target)) setAccountOpen(false); };
    document.addEventListener('mousedown', outside);
    accountItems.current.filter(Boolean)[0]?.focus();
    return () => document.removeEventListener('mousedown', outside);
  }, [accountOpen]);

  const onMessagesPage = location.pathname.startsWith('/app/messages');

  useEffect(() => {
    if (!user) return undefined;
    let cancel = false;
    const fetchBadge = async () => {
      try {
        const res = await axios.get(`${API}/propositions/badge`);
        if (!cancel) setPropsBadge(res.data?.pending || 0);
      } catch { /* silent */ }
    };
    fetchBadge();
    const iv = setInterval(fetchBadge, 60000);
    return () => { cancel = true; clearInterval(iv); };
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    let cancel = false;
    const fetchMsgBadge = async () => {
      try {
        const res = await axios.get(`${API}/conversations`);
        const total = (res.data || []).reduce((s, c) => s + (c.unread_count || 0), 0);
        if (!cancel && !window.location.pathname.startsWith('/app/messages')) setMsgBadge(total);
      } catch { /* silent */ }
    };
    fetchMsgBadge();
    const iv = setInterval(fetchMsgBadge, 30000);
    return () => { cancel = true; clearInterval(iv); };
  }, [user]);

  useEffect(() => { if (onMessagesPage) setMsgBadge(0); }, [onMessagesPage]);

  const isSender = user?.roles?.includes('ROLE_SENDER');
  const isCarrier = user?.roles?.includes('ROLE_CARRIER');
  const isHybrid = isSender && isCarrier;
  const roleView = isCarrier && !isSender ? 'carrier' : 'sender';
  const [profileView, setProfileView] = useState(
    isHybrid ? (user?.active_role === 'ROLE_CARRIER' ? 'carrier' : 'sender') : roleView,
  );
  useEffect(() => {
    setProfileView(isHybrid ? (user?.active_role === 'ROLE_CARRIER' ? 'carrier' : 'sender') : roleView);
  }, [user, isHybrid, roleView]);
  const meSide = profileView;

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const onAnnonces = (type) => location.pathname === '/annonces' && location.search.includes(`type=${type}`);

  const badges = { propositions: propsBadge, messages: onMessagesPage ? 0 : msgBadge };

  // Pastilles de l'en-tête, dans l'ordre de la maquette ; `phoneTab` : onglet de la barre du bas sur téléphone
  const links = [
    { key: 'dashboard', to: '/app/dashboard', label: 'Accueil', icon: LayoutDashboard, active: isActive('/app/dashboard'), phoneTab: true },
    meSide === 'sender'
      ? { key: 'shipments', to: '/app/shipments', label: 'Mes colis', icon: Package, active: isActive('/app/shipments'), show: isSender }
      : { key: 'trips', to: '/app/my-trips', label: 'Mes trajets', icon: Route, active: isActive('/app/my-trips') || isActive('/app/trips') },
    meSide === 'sender'
      ? { key: 'search-trips', to: '/annonces?type=trajet', label: 'Trouver un trajet', icon: Search, active: onAnnonces('trajet') }
      : { key: 'search-shipments', to: '/annonces?type=colis', label: 'Trouver des colis', icon: Search, active: onAnnonces('colis') },
    { key: 'propositions', to: '/app/propositions', label: 'Propositions', icon: Send, active: isActive('/app/propositions'), badge: 'propositions', phoneTab: true },
    { key: 'missions', to: '/app/missions', label: 'Missions', icon: ListChecks, active: isActive('/app/missions'), phoneTab: true },
    { key: 'messages', to: '/app/messages', label: 'Messages', icon: MessagesSquare, active: isActive('/app/messages'), badge: 'messages', phoneTab: true },
    meSide === 'carrier' && { key: 'payouts', to: '/app/versements', label: 'Mes versements', icon: Landmark, active: isActive('/app/versements') },
  ].filter((l) => l && l.show !== false);

  // Menu du compte : le compte, l'argent, et pour un compte hybride l'autre côté sans changer de vue
  const accountLinks = [
    { key: 'profile', to: '/app/profile', label: 'Profil', icon: User },
    { key: 'verifications', to: '/app/verifications', label: 'Mes vérifications', icon: ShieldCheck },
    { key: 'security', to: '/app/securite', label: 'Sécurité du compte', icon: KeyRound },
    meSide === 'carrier'
      ? { key: 'wallet', to: '/app/wallet', label: 'Revenus', icon: Banknote }
      : { key: 'wallet', to: '/app/wallet', label: 'Dépenses', icon: CreditCard },
    isCarrier && { key: 'subscription', to: '/app/abonnement', label: 'Abonnement PRO', icon: Crown },
    isHybrid && meSide === 'carrier' && { key: 'shipments', to: '/app/shipments', label: 'Mes colis', icon: Package },
    isHybrid && meSide === 'sender' && { key: 'trips', to: '/app/my-trips', label: 'Mes trajets', icon: Route },
    isHybrid && meSide === 'sender' && { key: 'payouts', to: '/app/versements', label: 'Mes versements', icon: Landmark },
    { key: 'role-requests', to: '/app/role-requests', label: 'Demandes de rôles', icon: UserPlus },
  ].filter(Boolean);

  const handleSwitchProfile = async (p) => {
    if (!isHybrid || p === profileView || switching) return;
    setSwitching(true);
    try {
      await switchRole(p === 'sender' ? 'ROLE_SENDER' : 'ROLE_CARRIER');
      setProfileView(p);
    } catch (e) {
      console.error(e);
    } finally {
      setSwitching(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      toast.error(LOGOUT_FAILED);
      return;
    }
    navigate('/');
  };

  const onAccountKey = (e) => {
    const items = accountItems.current.filter(Boolean);
    const i = items.indexOf(document.activeElement);
    if (e.key === 'Escape') { setAccountOpen(false); accountRef.current?.querySelector('.ah-avatar')?.focus(); }
    if (e.key === 'ArrowDown') { e.preventDefault(); items[(i + 1) % items.length]?.focus(); }
    if (e.key === 'ArrowUp') { e.preventDefault(); items[(i - 1 + items.length) % items.length]?.focus(); }
  };

  const viewSwitcher = (testPrefix = 'profile') => (
    <div className="ah-switch" role="group" aria-label="Je navigue en tant que">
      {['sender', 'carrier'].map((p) => (
        <button
          key={p}
          type="button"
          className={`ah-switch-btn${profileView === p ? ' is-on' : ''}`}
          aria-pressed={profileView === p}
          onClick={() => { handleSwitchProfile(p); setSheetOpen(false); }}
          data-testid={`${testPrefix}-${p}`}
          disabled={switching}
        >
          {VIEW_LABEL[p]}
        </button>
      ))}
    </div>
  );

  const badgeOf = (l, cls) => {
    const n = l.badge ? badges[l.badge] : 0;
    if (!n) return null;
    return <span className={`${cls}${l.badge === 'propositions' ? ' is-strong' : ''}`} data-testid={`nav-${l.key}-badge`}>{badgeText(n)}</span>;
  };

  const roleLine = `${VIEW_LABEL[profileView]}${isEntreprise(user) ? ' · Entreprise' : ''}${user?.pro_subscribed ? ' · PRO' : ''}`;
  const phoneTabs = links.filter((l) => l.phoneTab);
  const sheetLinks = links.filter((l) => !l.phoneTab);

  return (
    <div className="dash shell-c" data-testid="app-shell" data-role={profileView}>
      <header className="ah">
        <nav className="ah-nav" aria-label="Menu de l'application">
          <Link to="/app/dashboard" className="ah-logo" data-testid="nav-logo" aria-label="Colis2Ship, accueil">
            colis<span className="ah-logo-2">2</span>ship
          </Link>

          {!isPhone && (
            <ul className="ah-links">
              {links.map((l) => (
                <li key={l.key}>
                  <Link
                    to={l.to}
                    className={`ah-link${l.active ? ' is-on' : ''}`}
                    aria-current={l.active ? 'page' : undefined}
                    data-testid={`nav-${l.key}`}
                  >
                    {l.label}
                    {badgeOf(l, 'ah-badge')}
                  </Link>
                </li>
              ))}
            </ul>
          )}

          <div className="ah-right">
            {isHybrid && !isPhone && viewSwitcher()}
            <NotificationsDropdown user={user} />
            <div className="ah-account" ref={accountRef} onKeyDown={onAccountKey}>
              <button
                type="button"
                className="ah-avatar"
                aria-haspopup="menu"
                aria-expanded={accountOpen}
                aria-label={`Mon compte, ${user?.first_name || ''} ${user?.last_name || ''}`.trim()}
                onClick={() => setAccountOpen((o) => !o)}
                data-testid="nav-account"
              >
                <span aria-hidden="true">{getInitials(user)}</span>
                <ChevronDown size={14} className="ah-avatar-chev" aria-hidden="true" />
              </button>
              <div className="ah-menu" role="menu" aria-label="Mon compte" hidden={!accountOpen} data-testid="account-menu">
                <div className="ah-menu-head">
                  <div className="ah-menu-name">{user?.first_name} {user?.last_name}</div>
                  <div className="ah-menu-role">{roleLine}</div>
                </div>
                {accountLinks.map((l, i) => (
                  <Link
                    key={l.key}
                    to={l.to}
                    role="menuitem"
                    className={`ah-menu-item${isActive(l.to) ? ' is-on' : ''}`}
                    aria-current={isActive(l.to) ? 'page' : undefined}
                    ref={(el) => { accountItems.current[i] = el; }}
                    data-testid={`nav-${l.key}`}
                  >
                    <l.icon size={16} aria-hidden="true" />
                    {l.label}
                  </Link>
                ))}
                <button
                  type="button"
                  role="menuitem"
                  className="ah-menu-item ah-menu-logout"
                  onClick={handleLogout}
                  ref={(el) => { accountItems.current[accountLinks.length] = el; }}
                  data-testid="btn-logout"
                >
                  <LogOut size={16} aria-hidden="true" />
                  Déconnexion
                </button>
              </div>
            </div>
          </div>
        </nav>
      </header>

      <main className="content">
        {children}
      </main>

      {isPhone && (
        <nav className="bb" aria-label="Navigation principale">
          {phoneTabs.map((l) => (
            <Link
              key={l.key}
              to={l.to}
              className={`bb-tab${l.active ? ' is-on' : ''}`}
              aria-current={l.active ? 'page' : undefined}
              data-testid={`nav-${l.key}`}
            >
              <l.icon size={20} aria-hidden="true" />
              <span>{l.key === 'dashboard' ? 'Accueil' : l.label}</span>
              {badgeOf(l, 'bb-badge')}
            </Link>
          ))}
          <button
            type="button"
            className={`bb-tab${sheetOpen ? ' is-on' : ''}`}
            aria-expanded={sheetOpen}
            aria-controls="bb-sheet"
            onClick={() => setSheetOpen((o) => !o)}
            data-testid="bb-more"
          >
            <Ellipsis size={20} aria-hidden="true" />
            <span>Plus</span>
          </button>
        </nav>
      )}

      {isPhone && sheetOpen && (
        <>
          <div className="bs-overlay" onClick={() => setSheetOpen(false)} aria-hidden="true" />
          <div className="bs-sheet" id="bb-sheet" role="dialog" aria-modal="true" aria-label="Plus de pages">
            {isHybrid && viewSwitcher()}
            {sheetLinks.map((l) => (
              <Link
                key={l.key}
                to={l.to}
                className={`bs-item${l.active ? ' is-on' : ''}`}
                aria-current={l.active ? 'page' : undefined}
                data-testid={`nav-${l.key}`}
              >
                <l.icon size={18} aria-hidden="true" />
                {l.label}
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
